// packages/observability/src/exporters.ts
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { AggregationTemporality } from "@opentelemetry/sdk-metrics";

export interface ExporterOverrides {
  endpoint?: string;
  headers?: Record<string, string>;
}

export function buildOtlpHeaders(): Record<string, string> {
  const raw = process.env.OTEL_EXPORTER_OTLP_HEADERS ?? "";
  const headers: Record<string, string> = {};
  for (const part of raw.split(",")) {
    const separator = part.indexOf("=");
    if (separator <= 0) {
      continue;
    }
    const key = part.slice(0, separator).trim();
    const rawValue = part.slice(separator + 1).trim();
    if (!key || !rawValue) {
      continue;
    }
    try {
      headers[key] = decodeURIComponent(rawValue);
    } catch {
      headers[key] = rawValue;
    }
  }
  return headers;
}

export function getOtlpEndpoint(): string {
  // Set in repo-root .env:
  //   OTEL_EXPORTER_OTLP_ENDPOINT=https://signoz.atriam.ai/v1
  // For gateways co-located on droplet, override with:
  //   OTEL_EXPORTER_OTLP_ENDPOINT=http://localhost:4318/v1
  return process.env.OTEL_EXPORTER_OTLP_ENDPOINT ?? "http://localhost:4318/v1";
}

// On Vercel, HTTP keep-alive is poison for the OTLP exporters: the function
// freezes between invocations, the kept-alive socket idles past Caddy's
// timeout and dies, and the next flush reuses the dead socket → ECONNRESET →
// the batch is dropped with only a diag-level error. Empirically (2026-06-10)
// server log delivery degraded to ~1 batch per instance cold-start (~hourly)
// while every warm-instance flush silently failed. Same failure mode the
// /api/otlp proxy hit (fixed by dropping keepalive there, 6203f63d). The
// long-running Bun gateways keep keep-alive — their steady export cadence
// keeps sockets genuinely warm.
function keepAlive(): boolean {
  return !process.env.VERCEL;
}

export function makeTraceExporter(overrides?: ExporterOverrides) {
  const endpoint = overrides?.endpoint ?? getOtlpEndpoint();
  const headers = overrides?.headers ?? buildOtlpHeaders();
  return new OTLPTraceExporter({
    url: `${endpoint}/traces`,
    headers,
    keepAlive: keepAlive(),
  });
}

export function makeLogExporter(overrides?: ExporterOverrides) {
  const endpoint = overrides?.endpoint ?? getOtlpEndpoint();
  const headers = overrides?.headers ?? buildOtlpHeaders();
  return new OTLPLogExporter({
    url: `${endpoint}/logs`,
    headers,
    keepAlive: keepAlive(),
  });
}

export function makeMetricExporter(overrides?: ExporterOverrides) {
  const endpoint = overrides?.endpoint ?? getOtlpEndpoint();
  const headers = overrides?.headers ?? buildOtlpHeaders();
  return new OTLPMetricExporter({
    url: `${endpoint}/metrics`,
    headers,
    keepAlive: keepAlive(),
    // Delta is correct for short-lived serverless functions: each flush ships
    // only the increments since the last export, so a frozen function that
    // never exports again doesn't strand a cumulative total.
    temporalityPreference: AggregationTemporality.DELTA,
  });
}
